import { useEffect, useState } from 'react'
import stores from '../../../stores/store'
import SearchInput from '../../inputs/searchInput'
import {
  StyledItem,
  StyledItemDescription,
  StyledItemHead,
  StyledItemSubtitle,
  StyledItemText,
  StyledItemTitle,
  StyledLink,
  StyledPaper,
  StyledItemQuotes,
  StyledScrollPaper,
  StyledSearchBox
} from './styles'
import { ClickAwayListener, Grow } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { fetchData } from '../../../sdk'

type SearchItem = {
  servico: string
  titulo: string
  descricao: string
  path: string
  ocorrencias: number
  trecho: string
}

export function SearchHeader() {
  const { localeSelected, jsonSelected: lang } = stores.DataLocaleStore()
  const [searchItems, setSearchItems] = useState<SearchItem[]>([])
  const [valueSearch, setValueSearch] = useState('')

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search).toString()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClose = () => {
    setAnchorEl(null)

    setTimeout(() => {
      setValueSearch('')
    }, 200)
  }

  useEffect(() => {
    if (valueSearch.length > 0) {
      setAnchorEl(document.getElementById('search-input'))
    } else {
      setAnchorEl(null)
    }
  }, [valueSearch])

  const highlightText = (text: string, highlight: string) => {
    const regex = new RegExp(`(${highlight})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span
          key={index}
          style={{
            textDecoration: 'underline',
            fontWeight: 'bold',
            color: '#E53535'
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  const renderDescriptionWithHighlight = (
    description: string,
    highlight: string
  ) => {
    const highlightedDescription = description
      .split(new RegExp(`(${highlight})`, 'gi'))
      .map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase()
          ? `<span style="text-decoration: underline; font-weight: bold;color: #E53535;">${part}</span>`
          : part
      )
      .join('')

    return (
      <div
        dangerouslySetInnerHTML={{
          __html: highlightedDescription
        }}
      />
    )
  }

  function fetchSearchItems() {
    if (valueSearch.length < 3) {
      setSearchItems([])
      return
    }

    let url = `sandbox/v1/busca_produtos_api_docs?q=${encodeURIComponent(valueSearch)}`

    if (localeSelected === 'EN') {
      url += '&lingua=INGLESA'
    }

    fetchData(url)
      .then((data) => {
        if (data.code === '000') {
          setSearchItems(data.content)
        } else {
          setSearchItems([])
        }
      })
      .catch(() => {
        setSearchItems([])
      })
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchSearchItems()
    }, 400)

    return () => clearTimeout(timeout)
  }, [valueSearch])

  return (
    <>
      <SearchInput
        id="search-input"
        onValueChange={setValueSearch}
        value={valueSearch}
        placeholder={lang.header.search}
      />
      <StyledSearchBox
        open={open}
        anchorEl={anchorEl}
        placement="bottom"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'right top' : 'right bottom'
            }}
          >
            <div>
              <ClickAwayListener onClickAway={handleClose}>
                <StyledPaper>
                  <StyledScrollPaper>
                    {valueSearch.length < 3 ? (
                      <StyledItemTitle>
                        {lang.header.searchMinLength}
                      </StyledItemTitle>
                    ) : searchItems.length === 0 ? (
                      <StyledItemTitle>
                        {`${lang.header.searchEmpty} '${valueSearch}'`}
                      </StyledItemTitle>
                    ) : (
                      searchItems.map((item: SearchItem, index: number) => (
                        <StyledLink
                          to={`/${item.path}${queryParams ? `?${queryParams}` : ''}`}
                          key={index}
                        >
                          <StyledItem>
                            <StyledItemHead>
                              <StyledItemText>
                                <StyledItemTitle>
                                  {highlightText(item.titulo, valueSearch)}
                                </StyledItemTitle>
                                <StyledItemSubtitle>
                                  • {highlightText(item.servico, valueSearch)}
                                </StyledItemSubtitle>
                              </StyledItemText>

                              <StyledItemQuotes>
                                {lang.header.searchOccurrences}:
                                <b>{item.ocorrencias}</b>
                              </StyledItemQuotes>
                            </StyledItemHead>
                            {item.trecho && (
                              <StyledItemDescription>
                                {renderDescriptionWithHighlight(
                                  item.trecho,
                                  valueSearch
                                )}
                              </StyledItemDescription>
                            )}
                          </StyledItem>
                        </StyledLink>
                      ))
                    )}
                  </StyledScrollPaper>
                </StyledPaper>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </StyledSearchBox>
    </>
  )
}
